import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FaBook, FaSearch, FaFileExcel,
  FaTimes, FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import * as XLSX from 'xlsx-js-style';
import api from '../../../../utils/api';
import useColleges from '../../../../utils/useColleges';
import { useAuth } from '../../../../context/AuthContext';
import { buildStudentDrLedger, getAdmissionBatchFromRow } from '../tallyBatchHelpers';
import './JournalFees.css';

const isTransportRow = (row) =>
  !!row.isTransport ||
  !!row.transportJoinDate ||
  (row.feeName || '').toLowerCase().includes('transport');

/**
 * Journal date:
 * - Transport → join date
 * - Fee structure → Coverage From (durationFrom), not when student was assigned
 */
const getJournalDate = (row) => {
  if (isTransportRow(row)) {
    return row.transportJoinDate || null;
  }
  return row.coverageFrom || row.feeStructure?.durationFrom || null;
};

const getDrLedger = (row) => {
  const studentName = (row.student?.name || '').trim();
  const fatherName = (row.student?.fatherName || '').trim();
  const batch = getAdmissionBatchFromRow(row);
  return buildStudentDrLedger(studentName, fatherName, batch);
};

const LATE_FEE_COLUMN = 'LATE FEE';

const getLateFeeAmount = (row) => Number(row.lateFee) || 0;

/** Sum of all fee head amounts (tuition, transport, etc.) */
const getFeeItemsTotal = (row) =>
  (row.items || []).reduce((sum, i) => sum + (Number(i.amount) || 0), 0);

/** Amount Cr = fee particulars total + late fee */
const getAmountCr = (row) => {
  const itemsSum = getFeeItemsTotal(row);
  const base = itemsSum > 0 ? itemsSum : Number(row.totalAmount) || 0;
  return base + getLateFeeAmount(row);
};

const formatCurrency = (val) => {
  if (val === undefined || val === null || val === '' || Number(val) === 0) return '';
  return `₹ ${Number(val).toLocaleString()}`;
};

const formatLateFee = (row) => {
  const amt = getLateFeeAmount(row);
  return amt > 0 ? formatCurrency(amt) : '';
};

const JournalFees = () => {
  const { user } = useAuth();
  const { colleges: collegesList, loading: collegesLoading } = useColleges();
  const isScoped = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Auto-set college for scoped users
  useEffect(() => {
    if (isScoped && collegesList.length > 0 && !filterCollege) {
      setFilterCollege(collegesList[0]._id);
    }
  }, [isScoped, collegesList, filterCollege]);

  // Fetch real allocations from database
  const fetchRecords = useCallback(async () => {
    if (!filterCollege) {
      setRecords([]);
      return;
    }
    setLoading(true);
    setRecords([]);

    try {
      const { data } = await api.get('/fees/allocations', {
        params: { college: filterCollege, _t: Date.now() },
      });

      let allocations = (data?.data || []).filter(
        (a) => String(a.college?._id || a.college) === String(filterCollege)
      );

      // Filter by date range (coverage from / transport join date)
      if (fromDate || toDate) {
        allocations = allocations.filter((row) => {
          const dateToCompare = getJournalDate(row);
          if (!dateToCompare) return false;
          const d = new Date(dateToCompare);
          if (fromDate) {
            const f = new Date(fromDate);
            f.setHours(0, 0, 0, 0);
            if (d < f) return false;
          }
          if (toDate) {
            const t = new Date(toDate);
            t.setHours(23, 59, 59, 999);
            if (d > t) return false;
          }
          return true;
        });
      }

      allocations.sort((a, b) => {
        const dateA = new Date(getJournalDate(a) || 0);
        const dateB = new Date(getJournalDate(b) || 0);
        return dateA - dateB;
      });
      
      setRecords(allocations);
    } catch (error) {
      console.error('Error fetching journal fee allocations:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [filterCollege, fromDate, toDate]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleClearFilters = () => {
    setSearchQuery('');
    if (!isScoped) setFilterCollege('');
    setFromDate('');
    setToDate('');
    if (!isScoped) setRecords([]);
  };

  // Perform search matching constructed Dr Ledger details or admission number
  const filteredRecords = useMemo(() => {
    const search = searchQuery.toLowerCase().trim();
    return records.filter((row) => {
      if (!search) return true;
      const drLedger = getDrLedger(row).toLowerCase();
      const admissionNo = (row.student?.admissionNo || '').toLowerCase();
      return drLedger.includes(search) || admissionNo.includes(search);
    });
  }, [records, searchQuery]);

  const selectedCollege = useMemo(
    () => collegesList.find((c) => String(c._id) === String(filterCollege)),
    [collegesList, filterCollege]
  );

  const dynamicColumns = useMemo(() => {
    const headsSet = new Set();
    records.forEach((row) => {
      row.items?.forEach((i) => {
        if (i.type) headsSet.add(i.type.trim().toUpperCase());
      });
    });
    return Array.from(headsSet).sort();
  }, [records]);

  const showLateFeeColumn = useMemo(
    () => filteredRecords.some((row) => getLateFeeAmount(row) > 0),
    [filteredRecords]
  );

  // Compute column totals dynamically for UI summary block
  const totals = useMemo(() => {
    const columnTotals = {};
    dynamicColumns.forEach((col) => { columnTotals[col] = 0; });
    let crTotal = 0;
    let lateFeeTotal = 0;

    filteredRecords.forEach((row) => {
      crTotal += getAmountCr(row);
      lateFeeTotal += getLateFeeAmount(row);
      row.items?.forEach((i) => {
        if (i.type) {
          const colName = i.type.trim().toUpperCase();
          if (columnTotals[colName] !== undefined) {
            columnTotals[colName] += Number(i.amount) || 0;
          }
        }
      });
    });

    return { columnTotals, crTotal, lateFeeTotal };
  }, [filteredRecords, dynamicColumns]);

  const fmtDate = (d) => {
    if (!d) return '—';
    const dateObj = new Date(d);
    if (isNaN(dateObj.getTime())) return '—';
    return dateObj.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Export to Excel handling dynamic columns (with totals row removed)
  const handleExportExcel = () => {
    const headers = [
      'Date',
      'VCh No',
      'Dr Ledger',
      ...dynamicColumns,
      ...(showLateFeeColumn ? [LATE_FEE_COLUMN] : []),
      'Amount Cr',
      'Narations',
    ];

    const rows = filteredRecords.map((row) => {
      const itemMap = {};
      row.items?.forEach((i) => {
        if (i.type) itemMap[i.type.trim().toUpperCase()] = Number(i.amount) || '';
      });

      const dateVal = getJournalDate(row);

      return [
        dateVal ? new Date(dateVal).toLocaleDateString('en-GB') : '',
        '',
        getDrLedger(row),
        ...dynamicColumns.map((col) => itemMap[col] || ''),
        ...(showLateFeeColumn ? [getLateFeeAmount(row) || ''] : []),
        getAmountCr(row) || '',
        '',
      ];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    ws['!cols'] = [
      { wch: 12 },
      { wch: 12 },
      { wch: 45 },
      ...dynamicColumns.map(() => ({ wch: 15 })),
      ...(showLateFeeColumn ? [{ wch: 14 }] : []),
      { wch: 15 },
      { wch: 15 },
    ];

    const filenameParts = ['Journal_Fees_Register'];
    if (selectedCollege) {
      const cleanName = selectedCollege.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/__+/g, '_');
      filenameParts.push(cleanName);
    }
    if (fromDate && toDate) filenameParts.push(`${fromDate}_to_${toDate}`);
    else if (fromDate) filenameParts.push(`from_${fromDate}`);
    else if (toDate) filenameParts.push(`to_${toDate}`);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Journal Fees');
    XLSX.writeFile(wb, `${filenameParts.join('_')}.xlsx`);
  };

  const showSelectCollegeMsg = !filterCollege && !collegesLoading;

  return (
    <div className="tally-export-journal-fee-structure-container">
      <div className="tally-export-journal-fee-structure-header-row">
        <h2 className="tally-export-journal-fee-structure-title">
          <FaBook /> Journal Fee Structure Register
        </h2>
        <div className="tally-export-journal-fee-structure-export-btns">
          <button
            type="button"
            className="tally-export-journal-fee-structure-btn-excel"
            onClick={handleExportExcel}
            disabled={filteredRecords.length === 0}
          >
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      {filterCollege && filteredRecords.length > 0 && (
        <div className="tally-export-journal-fee-structure-summary-strip">
          <span>
            Showing <strong>{filteredRecords.length}</strong> student journal entries
            {selectedCollege ? <> for <strong>{selectedCollege.name}</strong></> : null}
          </span>
          <div className="tally-export-journal-fee-structure-summary-amounts">
            <span className="tally-export-journal-fee-structure-summary-amount">
              Total Credit: <strong>₹ {totals.crTotal.toLocaleString()}</strong>
            </span>
          </div>
        </div>
      )}

      <div className="tally-export-journal-fee-structure-controls">
        <div className="tally-export-journal-fee-structure-search-wrapper">
          <FaSearch className="tally-export-journal-fee-structure-search-icon" />
          <input
            type="text"
            className="tally-export-journal-fee-structure-search-input"
            placeholder="Search student ledger, admission number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={!filterCollege}
          />
        </div>

        {!isScoped && (
          <select
            className="tally-export-journal-fee-structure-select"
            value={filterCollege}
            onChange={(e) => setFilterCollege(e.target.value)}
            disabled={collegesLoading}
          >
            <option value="">Select College</option>
            {collegesList.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        {isScoped && selectedCollege && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 14px',
              background: '#f1f5f9',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#334155',
            }}
          >
            🏫 {selectedCollege.name}
          </span>
        )}

        <input
          type="date"
          className="tally-export-journal-fee-structure-select"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          title="From Date"
          disabled={!filterCollege}
        />

        <input
          type="date"
          className="tally-export-journal-fee-structure-select"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          min={fromDate}
          title="To Date"
          disabled={!filterCollege}
        />

        {(filterCollege && !isScoped) || fromDate || toDate || searchQuery ? (
          <button type="button" className="tally-export-journal-fee-structure-btn-clear" onClick={handleClearFilters}>
            <FaTimes /> Clear
          </button>
        ) : null}
      </div>

      <div className="tally-export-journal-fee-structure-grouped-container">
        {collegesLoading ? (
          <div className="tally-export-journal-fee-structure-state-msg">Loading colleges...</div>
        ) : showSelectCollegeMsg ? (
          <div className="tally-export-journal-fee-structure-state-msg tally-export-journal-fee-structure-empty">
            Please select a college to view journal fee structure allocations.
          </div>
        ) : loading ? (
          <div className="tally-export-journal-fee-structure-state-msg">Loading records...</div>
        ) : filteredRecords.length === 0 ? (
          <div className="tally-export-journal-fee-structure-state-msg tally-export-journal-fee-structure-empty">
            No record entry matches selected filters.
          </div>
        ) : (
          <div className="tally-export-journal-fee-structure-table-group">
            <h3 className="tally-export-journal-fee-structure-group-header">
              <span className="tally-export-journal-fee-structure-college-name">
                🏫 {selectedCollege?.name || 'College'}
              </span>
              <div className="tally-export-journal-fee-structure-group-stats">
                <span className="tally-export-journal-fee-structure-group-count">
                  {filteredRecords.length} entries
                </span>
                <span className="tally-export-journal-fee-structure-group-total">
                  Total Cr: ₹ {totals.crTotal.toLocaleString()}
                </span>
              </div>
            </h3>
            <div className="tally-export-journal-fee-structure-table-wrapper">
              <table className="tally-export-journal-fee-structure-table">
                <thead>
                  <tr>
                    <th width="55px">S.No</th>
                    <th className="tally-export-journal-fee-structure-col-date">Date</th>
                    <th className="tally-export-journal-fee-structure-col-vch">VCh No</th>
                    <th className="tally-export-journal-fee-structure-col-dr tally-export-journal-fee-structure-align-left">Dr Ledger</th>
                    
                    {/* Render Dynamic Fee Heads in Uppercase */}
                    {dynamicColumns.map((colName) => (
                      <th key={colName}>{colName}</th>
                    ))}

                    {showLateFeeColumn && (
                      <th className="tally-export-journal-fee-structure-col-late-fee">{LATE_FEE_COLUMN}</th>
                    )}

                    <th className="tally-export-journal-fee-structure-col-amount-cr">Amount Cr</th>
                    <th className="tally-export-journal-fee-structure-col-narration tally-export-journal-fee-structure-align-left">Narrations</th>
                    <th className="tally-export-journal-fee-structure-col-expand-header" width="70px">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((row, index) => {
                    // Populate mapped dynamic amounts
                    const itemMap = {};
                    row.items?.forEach((i) => {
                      if (i.type) {
                        itemMap[i.type.trim().toUpperCase()] = i.amount;
                      }
                    });

                    return (
                      <React.Fragment key={row._id || index}>
                        <tr className="tally-export-journal-fee-structure-main-row">
                          <td>{index + 1}</td>
                          <td className="tally-export-journal-fee-structure-col-date" style={{ fontWeight: 600, color: '#334155' }}>
                            {fmtDate(getJournalDate(row))}
                          </td>
                          <td className="tally-export-journal-fee-structure-col-vch" style={{ fontWeight: 600, color: '#94a3b8' }}>
                            {''}
                          </td>
                          <td className="tally-export-journal-fee-structure-col-dr tally-export-journal-fee-structure-align-left" style={{ fontWeight: 700, color: '#1e3a5f' }}>
                            {getDrLedger(row)}
                            <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>
                              {row.student?.admissionNo || ''}
                            </div>
                          </td>

                          {/* Render matching Dynamic Column Values */}
                          {dynamicColumns.map((colName) => (
                            <td key={colName}>
                              {formatCurrency(itemMap[colName])}
                            </td>
                          ))}

                          {showLateFeeColumn && (
                            <td className="tally-export-journal-fee-structure-col-late-fee">
                              {formatLateFee(row)}
                            </td>
                          )}

                          <td className="tally-export-journal-fee-structure-col-amount-cr tally-export-journal-fee-structure-amt-total">
                            {formatCurrency(getAmountCr(row))}
                          </td>
                          <td className="tally-export-journal-fee-structure-col-narration tally-export-journal-fee-structure-align-left">
                            {''}
                          </td>
                          <td className="tally-export-journal-fee-structure-col-expand-trigger">
                            <button
                              type="button"
                              className="tally-export-journal-fee-structure-expand-btn"
                              onClick={() => setExpandedRow(expandedRow === row._id ? null : row._id)}
                              aria-label="Toggle details"
                            >
                              {expandedRow === row._id ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                          </td>
                        </tr>

                        {expandedRow === row._id && (
                          <tr className="tally-export-journal-fee-structure-expanded-row">
                            <td colSpan={6 + dynamicColumns.length + (showLateFeeColumn ? 1 : 0)} style={{ padding: 0 }}>
                              <div className="tally-export-journal-fee-structure-expanded-content">
                                <div className="tally-export-journal-fee-structure-expanded-grid">
                                  <div className="tally-export-journal-fee-structure-exp-item tally-export-journal-fee-structure-exp-date">
                                    <span className="tally-export-journal-fee-structure-exp-label">
                                      {isTransportRow(row) ? 'Transport Join Date' : 'Coverage From'}
                                    </span>
                                    <span className="tally-export-journal-fee-structure-exp-value">{fmtDate(getJournalDate(row))}</span>
                                  </div>
                                  <div className="tally-export-journal-fee-structure-exp-item tally-export-journal-fee-structure-exp-vch">
                                    <span className="tally-export-journal-fee-structure-exp-label">VCh No</span>
                                    <span className="tally-export-journal-fee-structure-exp-value">{''}</span>
                                  </div>
                                  <div className="tally-export-journal-fee-structure-exp-item tally-export-journal-fee-structure-exp-dr">
                                    <span className="tally-export-journal-fee-structure-exp-label">Dr Ledger</span>
                                    <span className="tally-export-journal-fee-structure-exp-value">{getDrLedger(row)}</span>
                                  </div>
                                  
                                  {dynamicColumns.map((colName) => (
                                    itemMap[colName] > 0 && (
                                      <div className="tally-export-journal-fee-structure-exp-item" key={colName}>
                                        <span className="tally-export-journal-fee-structure-exp-label">{colName}</span>
                                        <span className="tally-export-journal-fee-structure-exp-value">{formatCurrency(itemMap[colName])}</span>
                                      </div>
                                    )
                                  ))}

                                  {showLateFeeColumn && (
                                    <div className="tally-export-journal-fee-structure-exp-item tally-export-journal-fee-structure-exp-late-fee">
                                      <span className="tally-export-journal-fee-structure-exp-label">{LATE_FEE_COLUMN}</span>
                                      <span className="tally-export-journal-fee-structure-exp-value">{formatLateFee(row)}</span>
                                    </div>
                                  )}

                                  <div className="tally-export-journal-fee-structure-exp-item tally-export-journal-fee-structure-exp-amount-cr">
                                    <span className="tally-export-journal-fee-structure-exp-label">Amount Cr</span>
                                    <span className="tally-export-journal-fee-structure-exp-value" style={{ color: '#16a34a', fontWeight: 700 }}>
                                      {formatCurrency(getAmountCr(row))}
                                    </span>
                                  </div>
                                  <div className="tally-export-journal-fee-structure-exp-item tally-export-journal-fee-structure-exp-narration">
                                    <span className="tally-export-journal-fee-structure-exp-label">Narrations</span>
                                    <span className="tally-export-journal-fee-structure-exp-value">{''}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JournalFees;